import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { clientsApi } from './clients.api';
import { clientKeys } from './clients.queries';
import type {
  CreateClientRequest,
  UpdateClientRequest,
  ClientsListParams
} from '../types';

export function useCreateClientMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('clients');

  return useMutation({
    mutationFn: ({
      onProgress,
      ...data
    }: CreateClientRequest & { onProgress?: (n: number) => void }) =>
      clientsApi.create(data, onProgress),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('createSuccess'),
      skipGlobalErrorToast: true
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    }
  });
}

export function useUpdateClientMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('clients');

  return useMutation({
    mutationFn: ({
      id,
      data,
      onProgress
    }: {
      id: number;
      data: UpdateClientRequest;
      onProgress?: (n: number) => void;
    }) => clientsApi.update(id, data, onProgress),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('updateSuccess'),
      skipGlobalErrorToast: true
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    }
  });
}

export function useDeleteClientMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('clients');

  return useMutation({
    mutationFn: (id: number) => clientsApi.delete(id),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('deleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    }
  });
}

export function useBulkDeleteClientsMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('clients');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[] }
        | { all: true; filters: Partial<ClientsListParams> }
    ) => clientsApi.bulkDelete(params),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('bulkDeleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    }
  });
}
